import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { User, LoginDto, RegisterDto } from '@/types/auth'
import { apiClient, ApiClientError } from '@/lib/api-client'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  validationErrors: Record<string, string[]> | null

  // Actions
  login: (credentials: LoginDto) => Promise<void>
  register: (data: RegisterDto) => Promise<void>
  logout: () => Promise<void>
  checkSession: () => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  resendVerificationEmail: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (
    token: string,
    password: string,
    passwordConfirm: string
  ) => Promise<void>
  googleSignIn: () => Promise<void>
  handleGoogleCallback: (code: string, state?: string) => Promise<void>
  clearError: () => void
  clearValidationErrors: () => void
  setLoading: (loading: boolean) => void
  resetAuthState: () => void
}

const SESSION_TIMEOUT = Number(import.meta.env.VITE_SESSION_TIMEOUT) || 43200000 // 12 hours to match backend
const REFRESH_INTERVAL =
  Number(import.meta.env.VITE_REFRESH_TOKEN_INTERVAL) || 3600000 // 1 hour refresh interval

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        validationErrors: null,

        login: async (credentials: LoginDto) => {
          set({ isLoading: true, error: null, validationErrors: null })
          try {
            const authResponse = await apiClient.login(credentials)
            set({
              user: authResponse.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              validationErrors: null,
            })

            // Start refresh token timer
            startRefreshTimer()
            // Start session activity monitoring
            resetSessionTimer()
          } catch (error) {
            if (error instanceof ApiClientError) {
              set({
                error: error.message,
                validationErrors: error.errors || null,
                isLoading: false,
                isAuthenticated: false,
                user: null,
              })
            } else {
              set({
                error: 'An unexpected error occurred. Please try again.',
                validationErrors: null,
                isLoading: false,
                isAuthenticated: false,
                user: null,
              })
            }
            throw error
          }
        },

        register: async (data: RegisterDto) => {
          set({ isLoading: true, error: null, validationErrors: null })
          try {
            const authResponse = await apiClient.register(data)
            set({
              user: authResponse.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              validationErrors: null,
            })

            // Start refresh token timer
            startRefreshTimer()
            // Start session activity monitoring
            resetSessionTimer()
          } catch (error) {
            if (error instanceof ApiClientError) {
              set({
                error: error.message,
                validationErrors: error.errors || null,
                isLoading: false,
                isAuthenticated: false,
                user: null,
              })
            } else {
              set({
                error: 'An unexpected error occurred. Please try again.',
                validationErrors: null,
                isLoading: false,
                isAuthenticated: false,
                user: null,
              })
            }
            throw error
          }
        },

        logout: async () => {
          set({ isLoading: true })
          try {
            await apiClient.logout()
          } catch (error) {
            console.error('Logout error:', error)
          } finally {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              validationErrors: null,
            })
            // Clear OAuth tokens processed flag to allow fresh authentication
            sessionStorage.removeItem('oauth_tokens_processed')
            stopRefreshTimer()
            stopSessionTimer()
          }
        },

        checkSession: async () => {
          const token = localStorage.getItem('access_token')
          if (!token) {
            set({ isAuthenticated: false, user: null, isLoading: false })
            return
          }

          set({ isLoading: true })
          try {
            const isValid = await apiClient.verifySession()
            if (isValid) {
              const user = await apiClient.getCurrentUser()
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              })
              startRefreshTimer()
              resetSessionTimer()
            } else {
              throw new Error('Invalid session')
            }
          } catch (error) {
            console.error('Session check failed:', error)
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            })
          }
        },

        verifyEmail: async (token: string) => {
          set({ isLoading: true, error: null })
          try {
            await apiClient.verifyEmail(token)
            // Refresh user data to get updated emailVerified status
            if (get().isAuthenticated) {
              const user = await apiClient.getCurrentUser()
              set({ user, isLoading: false })
            } else {
              set({ isLoading: false })
            }
          } catch (error) {
            set({
              error:
                error instanceof ApiClientError
                  ? error.message
                  : 'Email verification failed',
              isLoading: false,
            })
            throw error
          }
        },

        resendVerificationEmail: async () => {
          set({ isLoading: true, error: null })
          try {
            await apiClient.resendVerificationEmail()
            set({ isLoading: false })
          } catch (error) {
            set({
              error:
                error instanceof ApiClientError
                  ? error.message
                  : 'Failed to resend verification email',
              isLoading: false,
            })
            throw error
          }
        },

        forgotPassword: async (email: string) => {
          set({ isLoading: true, error: null, validationErrors: null })
          try {
            await apiClient.forgotPassword(email)
            set({ isLoading: false })
          } catch (error) {
            if (error instanceof ApiClientError) {
              set({
                error: error.message,
                validationErrors: error.errors || null,
                isLoading: false,
              })
            } else {
              set({
                error: 'Failed to send reset email. Please try again.',
                validationErrors: null,
                isLoading: false,
              })
            }
            throw error
          }
        },

        resetPassword: async (
          token: string,
          password: string,
          passwordConfirm: string
        ) => {
          set({ isLoading: true, error: null, validationErrors: null })
          try {
            await apiClient.resetPassword(token, password, passwordConfirm)
            set({ isLoading: false })
          } catch (error) {
            if (error instanceof ApiClientError) {
              // Handle specific backend error codes
              let errorMessage = error.message
              if (error.code === 'INVALID_RESET_TOKEN') {
                errorMessage =
                  'This reset link is invalid or has expired. Please request a new one.'
              } else if (error.code === 'PASSWORD_MISMATCH') {
                errorMessage = 'Passwords do not match.'
              }

              set({
                error: errorMessage,
                validationErrors: error.errors || null,
                isLoading: false,
              })
            } else {
              set({
                error: 'Failed to reset password. Please try again.',
                validationErrors: null,
                isLoading: false,
              })
            }
            throw error
          }
        },

        googleSignIn: async () => {
          set({ isLoading: true, error: null, validationErrors: null })
          try {
            console.log('Initiating Google OAuth...')
            const response = await apiClient.getGoogleAuthUrl()
            console.log('Google OAuth response:', response)

            if (response && response.url) {
              console.log('Redirecting to:', response.url)
              // Generate and store secure state parameter for CSRF protection
              if (response.state) {
                const stateHash = await crypto.subtle.digest(
                  'SHA-256',
                  new TextEncoder().encode(
                    response.state + window.location.origin
                  )
                )
                const secureStateHash = btoa(
                  String.fromCharCode(...new Uint8Array(stateHash))
                )
                sessionStorage.setItem('oauth_state_hash', secureStateHash)
                sessionStorage.setItem('oauth_state_original', response.state)
              }
              // Redirect to Google OAuth URL
              window.location.href = response.url
            } else {
              throw new Error('No OAuth URL received from server')
            }
          } catch (error) {
            console.error('Google OAuth error:', error)
            if (error instanceof ApiClientError) {
              if (error.code === 'NETWORK_ERROR') {
                set({
                  error:
                    'Backend server is not running. Please start the server on port 3000.',
                  isLoading: false,
                })
              } else {
                set({
                  error: error.message,
                  isLoading: false,
                })
              }
            } else {
              set({
                error: 'Failed to initiate Google sign in. Please try again.',
                isLoading: false,
              })
            }
            throw error
          }
        },

        handleGoogleCallback: async (code: string, state?: string) => {
          set({ isLoading: true, error: null, validationErrors: null })
          try {
            // Validate OAuth state parameter to prevent CSRF attacks
            if (state) {
              const storedOriginalState = sessionStorage.getItem(
                'oauth_state_original'
              )
              const storedStateHash = sessionStorage.getItem('oauth_state_hash')

              if (storedOriginalState && storedStateHash) {
                const expectedStateHash = await crypto.subtle.digest(
                  'SHA-256',
                  new TextEncoder().encode(
                    storedOriginalState + window.location.origin
                  )
                )
                const expectedSecureStateHash = btoa(
                  String.fromCharCode(...new Uint8Array(expectedStateHash))
                )

                if (
                  storedStateHash !== expectedSecureStateHash ||
                  storedOriginalState !== state
                ) {
                  throw new Error(
                    'Invalid OAuth state parameter - possible CSRF attack'
                  )
                }

                // Clean up stored state
                sessionStorage.removeItem('oauth_state_original')
                sessionStorage.removeItem('oauth_state_hash')
              } else {
                throw new Error('Missing OAuth state validation data')
              }
            }

            const authResponse = await apiClient.exchangeGoogleToken(
              code,
              state
            )
            set({
              user: authResponse.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              validationErrors: null,
            })

            // Start refresh token timer
            startRefreshTimer()
            // Start session activity monitoring
            resetSessionTimer()
          } catch (error) {
            if (error instanceof ApiClientError) {
              set({
                error: error.message,
                validationErrors: error.errors || null,
                isLoading: false,
                isAuthenticated: false,
                user: null,
              })
            } else {
              set({
                error:
                  error instanceof Error
                    ? error.message
                    : 'Google sign in failed. Please try again.',
                validationErrors: null,
                isLoading: false,
                isAuthenticated: false,
                user: null,
              })
            }
            throw error
          }
        },

        clearError: () => set({ error: null }),
        clearValidationErrors: () => set({ validationErrors: null }),
        setLoading: (loading: boolean) => set({ isLoading: loading }),
        resetAuthState: () =>
          set({
            isLoading: false,
            error: null,
            validationErrors: null,
          }),
      }),
      {
        name: 'auth-storage',
        partialize: state => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'auth-store' }
  )
)

// Refresh token timer management
let refreshTimer: ReturnType<typeof setInterval> | null = null

const startRefreshTimer = () => {
  stopRefreshTimer()
  refreshTimer = setInterval(async () => {
    try {
      await apiClient.refreshToken()
    } catch (error) {
      console.error('Token refresh failed:', error)
      // Only logout if it's a critical auth error, not network issues
      if (error instanceof Error && (error.message.includes('NO_REFRESH_TOKEN') || error.message.includes('401'))) {
        useAuthStore.getState().logout()
      }
      // For other errors (network issues), keep trying on next interval
    }
  }, REFRESH_INTERVAL)
}

const stopRefreshTimer = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

// Session timeout management
let sessionTimer: ReturnType<typeof setTimeout> | null = null

const resetSessionTimer = () => {
  stopSessionTimer()
  sessionTimer = setTimeout(() => {
    console.log('Session timeout due to inactivity')
    useAuthStore.getState().logout()
  }, SESSION_TIMEOUT)
}

const stopSessionTimer = () => {
  if (sessionTimer) {
    clearTimeout(sessionTimer)
    sessionTimer = null
  }
}

// Reset session timer on user activity
const activityEvents = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
]

const handleUserActivity = () => {
  const { isAuthenticated } = useAuthStore.getState()
  if (isAuthenticated) {
    resetSessionTimer()
  }
}

// Add activity listeners
if (typeof window !== 'undefined') {
  activityEvents.forEach(event => {
    document.addEventListener(event, handleUserActivity, true)
  })
}
